import React, { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import {
  XMarkIcon,
  HomeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { fetchAppointmentById } from "../store/thunks/appointmentsThunk";
import { fetchClinicResources } from "../store/thunks/resourceThunk";
import {
  checkResourceAvailability,
  addResourceToAppointment,
  removeResourceFromAppointment,
} from "../store/thunks/resourceScheduleThunk";

const ModifyResourceModal = ({ isOpen, onClose, appointmentId }) => {
  const dispatch = useDispatch();
  const appointment = useSelector(
    (state) => state.appointments.selectedAppointment
  );
  const availableResources = useSelector(
    (state) => state.resources.resources || []
  );

  const [selectedResources, setSelectedResources] = useState([]);
  const [resourceAvailability, setResourceAvailability] = useState({});
  const [resourceTypeFilter, setResourceTypeFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState("");

  // Fetch appointment details on mount
  useEffect(() => {
    if (isOpen && appointmentId) {
      setIsLoading(true);
      dispatch(fetchAppointmentById(appointmentId))
        .unwrap()
        .then((response) => {
          if (response.resources && response.resources.length > 0) {
            setSelectedResources(response.resources);
          }
          return response;
        })
        .catch((error) => {
          console.error("Failed to fetch appointment details:", error);
          setStatusMessage("Failed to load appointment details");
        });
    }
  }, [dispatch, appointmentId, isOpen]);

  // Fetch clinic resources when appointment is loaded
  useEffect(() => {
    if (appointment && appointment.clinic) {
      const clinicId = appointment.clinic._id || appointment.clinic;
      
      if (!clinicId) {
        console.error("Error: Could not determine clinic ID");
        setStatusMessage("Failed to determine clinic ID");
        return;
      }
      
      dispatch(fetchClinicResources(clinicId))
        .unwrap()
        .then(() => {
          console.log("Fetched clinic resources");
        })
        .catch((error) => {
          console.error("Failed to fetch clinic resources:", error);
          setStatusMessage("Failed to load resources");
        });
    }
  }, [dispatch, appointment]);

  // Check resource availability when appointment and resources are loaded
  useEffect(() => {
    if (
      appointment &&
      appointment.start &&
      appointment.service &&
      appointment.clinic
    ) {
      const clinicId = appointment.clinic._id || appointment.clinic;
      
      if (!clinicId) {
        console.error("Error: Could not determine clinic ID");
        setStatusMessage("Failed to determine clinic ID");
        setIsLoading(false);
        return;
      }
      
      dispatch(
        checkResourceAvailability({
          clinicId,
          start: appointment.start,
          duration: appointment.service.duration || 30,
        })
      )
        .unwrap()
        .then((availabilityData) => {
          // Create availability map
          const availabilityMap = {};
          availabilityData.forEach((resource) => {
            availabilityMap[resource.resourceId] = {
              bookingStatus: resource.bookingStatus,
              overlappingBookings: resource.overlappingBookings,
            };
          });

          // Mark already selected resources as available
          if (selectedResources.length > 0) {
            selectedResources.forEach((resource) => {
              availabilityMap[resource._id] = {
                bookingStatus: "Available",
                overlappingBookings: [],
              };
            });
          }

          setResourceAvailability(availabilityMap);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Failed to check resource availability:", error);
          setStatusMessage("Failed to check resource availability");
          setIsLoading(false);
        });
    }
  }, [dispatch, appointment, selectedResources]);

  const handleResourceSelection = async (resource) => {
    // Check if resource is already selected
    const isAlreadySelected = selectedResources.some(
      (r) => r._id === resource._id
    );

    // Make sure we have the clinic ID (it might be nested differently in the appointment object)
    const clinicId = appointment.clinic?._id || appointment.clinic;
    
    if (!clinicId) {
      setStatusMessage("Error: Could not determine clinic ID");
      return;
    }

    if (isAlreadySelected) {
      // Remove resource
      try {
        setStatusMessage("Removing resource...");
        const result = await dispatch(
          removeResourceFromAppointment({
            clinicId,
            appointmentId: appointment._id,
            resourceId: resource._id,
          })
        ).unwrap();

        // Update selected resources
        setSelectedResources((prev) =>
          prev.filter((r) => r._id !== resource._id)
        );

        setStatusMessage("Resource removed successfully");

        // Refresh appointment data
        dispatch(fetchAppointmentById(appointment._id));
      } catch (error) {
        console.error("Failed to remove resource:", error);
        setStatusMessage("Failed to remove resource");
      }
    } else {
      // Check availability (except for already selected resources)
      const availability = resourceAvailability[resource._id];
      if (!availability || availability.bookingStatus !== "Available") {
        setStatusMessage(
          "This resource is not available for the appointment time"
        );
        return;
      }

      // Add resource
      try {
        setStatusMessage("Adding resource...");
        const result = await dispatch(
          addResourceToAppointment({
            clinicId,
            appointmentId: appointment._id,
            resourceId: resource._id,
            start: appointment.start,
            end: appointment.end,
            title: appointment.title || "Appointment Resource",
          })
        ).unwrap();

        // Update selected resources
        setSelectedResources((prev) => [...prev, resource]);

        setStatusMessage("Resource added successfully");

        // Refresh appointment data
        dispatch(fetchAppointmentById(appointment._id));
      } catch (error) {
        console.error("Failed to add resource:", error);
        setStatusMessage("Failed to add resource");
      }
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <Dialog.Overlay className="fixed inset-0 bg-gray-900/50 transition-opacity" />

        {/* Centering trick */}
        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-sm">
          <div className="absolute right-4 top-4">
            <button
              type="button"
              className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={onClose}>
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="bg-white px-6 py-6">
            <Dialog.Title
              as="h3"
              className="text-xl font-semibold leading-6 text-gray-900 mb-6">
              Modify Appointment Resources
            </Dialog.Title>

            {statusMessage && (
              <div
                className={`mb-4 p-3 rounded-sm ${
                  statusMessage.includes("Failed")
                    ? "bg-red-50 text-red-700"
                    : statusMessage.includes("success")
                    ? "bg-green-50 text-green-700"
                    : "bg-blue-50 text-blue-700"
                }`}>
                {statusMessage.includes("Failed") ? (
                  <ExclamationCircleIcon className="inline-block h-5 w-5 mr-2" />
                ) : statusMessage.includes("success") ? (
                  <CheckCircleIcon className="inline-block h-5 w-5 mr-2" />
                ) : null}
                {statusMessage}
              </div>
            )}

            {isLoading ? (
              <div className="flex justify-center items-center h-40">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900">
                    Appointment Details
                  </h4>
                  {appointment && (
                    <div className="bg-gray-50 p-3 rounded-sm mt-2">
                      <p className="text-sm text-gray-600">
                        <strong>Title:</strong> {appointment.title}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Date & Time:</strong>{" "}
                        {format(
                          new Date(appointment.start),
                          "MMMM d, yyyy h:mm a"
                        )}{" "}
                        - {format(new Date(appointment.end), "h:mm a")}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Client:</strong> {appointment.client.firstName}{" "}
                        {appointment.client.lastName}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Clinician:</strong>{" "}
                        {appointment.clinician.firstName}{" "}
                        {appointment.clinician.lastName}
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-6 border-b border-gray-900/10 pb-6">
                  <div className="flex items-center space-x-2 mb-1.5">
                    <HomeIcon className="h-5 w-5 text-gray-500" />
                    <h3 className="text-base font-semibold text-gray-900">
                      Resources
                    </h3>
                  </div>

                  <div className="flex justify-between items-center mb-3">
                    <p className="text-sm text-gray-600">
                      Manage resources for this appointment
                    </p>
                    <select
                      value={resourceTypeFilter}
                      onChange={(e) => setResourceTypeFilter(e.target.value)}
                      className="rounded-sm border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm">
                      <option value="">All Resources</option>
                      <option value="Room">Rooms</option>
                      <option value="Equipment">Equipment</option>
                      <option value="Testing Material">
                        Testing Materials
                      </option>
                      <option value="Assistive Device">
                        Assistive Devices
                      </option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="space-y-4">
                    {Object.entries(
                      availableResources
                        .filter((resource) =>
                          resourceTypeFilter
                            ? resource.type === resourceTypeFilter &&
                              resource.status === "active"
                            : resource.status === "active"
                        )
                        .reduce((acc, resource) => {
                          if (!acc[resource.type]) acc[resource.type] = [];
                          acc[resource.type].push(resource);
                          return acc;
                        }, {})
                    ).map(([type, resources]) => (
                      <div key={type} className="bg-gray-50 p-3 rounded-sm">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          {type}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {resources.map((resource) => {
                            const availability =
                              resourceAvailability[resource._id];
                            const isSelected = selectedResources.some(
                              (r) => r._id === resource._id
                            );

                            const overlappingBookings =
                              availability?.overlappingBookings || [];
                            const bookingTimes =
                              overlappingBookings.length > 0
                                ? overlappingBookings
                                    .map(
                                      (booking) =>
                                        `${format(
                                          new Date(booking.start),
                                          "h:mm a"
                                        )} - ${format(
                                          new Date(booking.end),
                                          "h:mm a"
                                        )}`
                                    )
                                    .join(", ")
                                : null;

                            return (
                              <div
                                key={resource._id}
                                className="py-2 flex items-center">
                                <input
                                  id={`resource-${resource._id}`}
                                  name={`resource-${resource._id}`}
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() =>
                                    handleResourceSelection(resource)
                                  }
                                  disabled={
                                    !isSelected &&
                                    (!availability ||
                                      availability.bookingStatus !==
                                        "Available")
                                  }
                                  className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 ${
                                    !isSelected &&
                                    (!availability ||
                                      availability.bookingStatus !==
                                        "Available")
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                />
                                <label
                                  htmlFor={`resource-${resource._id}`}
                                  className="ml-3 block text-sm leading-6 text-gray-900">
                                  {resource.name}
                                  {resource.description &&
                                    ` - ${resource.description}`}
                                  {resource.location &&
                                    ` (${resource.location})`}
                                  <span
                                    className={`ml-2 ${
                                      isSelected
                                        ? "text-blue-600"
                                        : !availability
                                        ? "text-gray-400"
                                        : availability.bookingStatus ===
                                          "Available"
                                        ? "text-green-600"
                                        : availability.bookingStatus ===
                                          "Unavailable"
                                        ? "text-orange-600"
                                        : "text-red-600"
                                    }`}>
                                    (
                                    {isSelected
                                      ? "Currently Assigned"
                                      : availability
                                      ? availability.bookingStatus
                                      : "Loading..."}
                                    )
                                  </span>
                                  {availability?.bookingStatus ===
                                    "Unavailable" &&
                                    bookingTimes && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        Unavailable: {bookingTimes}
                                      </div>
                                    )}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-sm bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ModifyResourceModal;
